/* ==========================================================================
   TASK 260921T1D — type_collection_v2 Compare + Sticky Pin Panel.

   Lightweight comparison: select products from the rendered table, sticky
   tray while scrolling, side-by-side comparison panel.

   Reads only from already-rendered DOM cells. No AJAX, no duplicate data.
   Storage: localStorage key rf_tcv2_compare_<collection handle>.
   Different from T1C's rf_tcv_columns_ key.

   Selection limits:
     Desktop (>=1024px): max 4
     Tablet (768–1023px):  max 3
     Mobile (<768px):      max 3
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY_PREFIX = 'rf_tcv2_compare_';
  var TABLE_SEL = '[data-tcv-table]';
  var ROW_SEL = 'tr.rf-tcv__row';
  var BRAND_ROW_SEL = 'tr.rf-tcv__group-row';

  /* ---- viewport-based limits ---- */

  function getMaxSelection() {
    var w = window.innerWidth || document.documentElement.clientWidth;
    if (w >= 1024) return 4;
    if (w >= 768) return 3;
    return 3;
  }

  /* ---- state (in-memory, refreshed from storage on init) ---- */

  var state = {
    collection: null,
    selected: [],
    panelOpen: false
  };

  function storageKey() {
    return STORAGE_KEY_PREFIX + (state.collection || 'all');
  }

  function readState() {
    try {
      var raw = window.localStorage.getItem(storageKey());
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(function (product) {
        var table = document.querySelector(TABLE_SEL);
        var priceEnabled = table && table.getAttribute('data-tcv-price-enabled') === 'true';
        if (!priceEnabled) {
          delete product.priceText;
          if (product.specs) delete product.specs.price;
        }
        return product;
      });
    } catch (e) {
      return [];
    }
  }

  function writeState() {
    try {
      if (state.selected.length) {
        window.localStorage.setItem(storageKey(), JSON.stringify(state.selected));
      } else {
        window.localStorage.removeItem(storageKey());
      }
    } catch (e) {
      /* private mode */
    }
  }

  /* ---- data extraction from rendered rows ---- */

  function extractProductData(row) {
    var link = row.querySelector('.rf-tcv__product-link');
    if (!link) return null;

    var nameEl = row.querySelector('.rf-tcv__name');
    var secondaryEl = row.querySelector('.rf-tcv__secondary');
    var thumbImg = row.querySelector('.rf-tcv__thumb-img');

    var href = link.getAttribute('href') || '';
    // Extract handle from URL like /products/some-handle
    var handle = href.replace(/^\/products\//, '').replace(/\?.*$/, '');
    // Get product ID from href if available, otherwise use a stable fallback
    var productId = handle;

    var cells = row.querySelectorAll('[data-col]');
    var specs = {};
    for (var i = 0; i < cells.length; i++) {
      var col = cells[i].getAttribute('data-col');
      var text = cells[i].textContent.trim();
      if (text) specs[col] = text;
    }
    var sizeNodes = row.querySelectorAll('[data-tcv-product-size]');
    var sizes = [];
    for (var j = 0; j < sizeNodes.length; j++) {
      var sizeText = sizeNodes[j].textContent.trim();
      if (sizeText) sizes.push(sizeText);
    }
    if (sizes.length) specs.size = sizes.join('\n');

    return {
      id: productId,
      handle: handle,
      title: nameEl ? nameEl.textContent.trim() : '',
      secondary: secondaryEl ? secondaryEl.textContent.trim() : '',
      imageSrc: thumbImg ? thumbImg.getAttribute('src') : null,
      imageAlt: thumbImg ? thumbImg.getAttribute('alt') : '',
      specs: specs
    };
  }

  function findRowById(id) {
    var table = document.querySelector(TABLE_SEL);
    if (!table) return null;
    var rows = table.querySelectorAll(ROW_SEL);
    for (var i = 0; i < rows.length; i++) {
      var link = rows[i].querySelector('.rf-tcv__product-link');
      if (!link) continue;
      var href = link.getAttribute('href') || '';
      var handle = href.replace(/^\/products\//, '').replace(/\?.*$/, '');
      if (handle === id || href.indexOf('/' + id + '?') !== -1 || href.endsWith('/' + id)) {
        return rows[i];
      }
    }
    return null;
  }

  /* ---- Compare checkbox in each product row ---- */

  function addCompareCheckboxes(table) {
    var rows = table.querySelectorAll(ROW_SEL);
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (row.querySelector('[data-tcv-compare-toggle]')) continue; // already added

      var productTh = row.querySelector('.rf-tcv__product');
      var compareCell = row.querySelector('[data-tcv-compare-cell]');
      if (!productTh || !compareCell) continue;

      var link = productTh.querySelector('.rf-tcv__product-link');
      if (!link) continue;

      var href = link.getAttribute('href') || '';
      var handle = href.replace(/^\/products\//, '').replace(/\?.*$/, '');

      var checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.setAttribute('data-tcv-compare-toggle', '');
      checkbox.setAttribute('data-tcv-product-id', handle);
      var nameEl = row.querySelector('.rf-tcv__name');
      var ariaLabel = nameEl ? 'Compare ' + nameEl.textContent.trim() : 'Compare this product';
      checkbox.setAttribute('aria-label', ariaLabel);
      checkbox.className = 'rf-tcv__compare-cb';

      compareCell.appendChild(checkbox);
    }
  }

  function syncCheckboxes() {
    var selectedIds = state.selected.map(function (p) { return p.id; });
    var cbs = document.querySelectorAll('[data-tcv-compare-toggle]');
    for (var i = 0; i < cbs.length; i++) {
      var id = cbs[i].getAttribute('data-tcv-product-id');
      cbs[i].checked = selectedIds.indexOf(id) !== -1;
    }
  }

  /* ---- fixed bottom compare bar ---- */

  function ensureTray() {
    var existing = document.querySelector('[data-tcv-compare-tray]');
    if (existing) return existing;
    if (state.selected.length === 0) return null;

    var tray = document.createElement('div');
    tray.setAttribute('data-tcv-compare-tray', '');
    tray.setAttribute('role', 'region');
    tray.setAttribute('aria-label', 'Compare selected products');
    tray.className = 'rf-tcv-compare-tray';

    var compareBtn = document.createElement('button');
    compareBtn.type = 'button';
    compareBtn.className = 'rf-tcv-compare-tray-btn';
    compareBtn.setAttribute('data-tcv-compare-open', '');
    compareBtn.textContent = compareLabel();
    compareBtn.disabled = state.selected.length < 2;

    var clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'rf-tcv-compare-tray-btn rf-tcv-compare-tray-btn--clear';
    clearBtn.setAttribute('data-tcv-compare-clear', '');
    clearBtn.textContent = 'Clear all';

    var summary = document.createElement('div');
    summary.className = 'rf-tcv-compare-summary';
    summary.innerHTML = '<span class="rf-tcv-compare-count" data-tcv-compare-count></span><span><strong>Compare products</strong><small data-tcv-compare-status></small></span>';

    var actions = document.createElement('div');
    actions.className = 'rf-tcv-compare-actions';
    actions.appendChild(clearBtn);
    actions.appendChild(compareBtn);

    tray.appendChild(summary);
    tray.appendChild(actions);

    document.body.appendChild(tray);
    return tray;
  }

  function compareLabel() {
    return state.selected.length ? 'Compare ' + state.selected.length : 'Compare';
  }

  function updateTray() {
    var tray = document.querySelector('[data-tcv-compare-tray]');
    if (state.selected.length === 0) {
      if (tray) tray.remove();
      return;
    }
    if (!tray) {
      tray = ensureTray();
    }

    var compareBtn = tray.querySelector('[data-tcv-compare-open]');
    if (compareBtn) {
      compareBtn.textContent = compareLabel();
      compareBtn.disabled = state.selected.length < 2;
    }
    var count = tray.querySelector('[data-tcv-compare-count]');
    if (count) count.textContent = state.selected.length;
    var status = tray.querySelector('[data-tcv-compare-status]');
    if (status) status.textContent = state.selected.length === 1 ? 'Select one more product' : state.selected.length + ' products selected';
  }

  /* ---- compare panel ---- */

  function buildPanel() {
    if (state.panelOpen) return;
    state.panelOpen = true;

    var overlay = document.createElement('div');
    overlay.className = 'rf-tcv-compare-overlay';
    overlay.setAttribute('data-tcv-compare-overlay', '');
    overlay.setAttribute('data-tcv-compare-close-bg', '');

    var panel = document.createElement('div');
    panel.className = 'rf-tcv-compare-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Compare products');
    panel.setAttribute('data-tcv-compare-panel', '');
    panel.setAttribute('aria-modal', 'true');

    // Header
    var header = document.createElement('div');
    header.className = 'rf-tcv-compare-panel-header';

    var title = document.createElement('h2');
    title.className = 'rf-tcv-compare-panel-title';
    title.textContent = 'Compare ' + state.selected.length + ' products';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'rf-tcv-compare-panel-close';
    closeBtn.setAttribute('data-tcv-compare-close', '');
    closeBtn.setAttribute('aria-label', 'Close comparison');
    closeBtn.textContent = '×';

    header.appendChild(title);
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // Body: comparison table
    var body = document.createElement('div');
    body.className = 'rf-tcv-compare-panel-body';

    // Collect all spec keys that have at least one value across selected products
    var allSpecKeys = [];
    var specLabels = {
      'thickness': 'Thickness',
      'size': 'Board Size',
      'pattern': 'Pattern',
      'colours': 'Colours',
      'water': 'Water Resistance',
      'ac': 'AC Rating',
      'price_range': 'Price Range',
      'price': 'Price'
    };

    for (var i = 0; i < state.selected.length; i++) {
      var specs = state.selected[i].specs;
      for (var key in specs) {
        if (specs.hasOwnProperty(key) && allSpecKeys.indexOf(key) === -1) {
          allSpecKeys.push(key);
        }
      }
    }

    // Build comparison table
    var compTable = document.createElement('table');
    compTable.className = 'rf-tcv-compare-table';

    // Header row with product names
    var thead = document.createElement('thead');
    var headerRow = document.createElement('tr');

    var thLabel = document.createElement('th');
    thLabel.className = 'rf-tcv-compare-label-col';
    thLabel.textContent = '';
    headerRow.appendChild(thLabel);

    for (var i = 0; i < state.selected.length; i++) {
      var product = state.selected[i];
      var th = document.createElement('th');
      th.className = 'rf-tcv-compare-product-header';

      if (product.imageSrc) {
        var img = document.createElement('img');
        img.src = product.imageSrc;
        img.alt = product.imageAlt || product.title;
        img.className = 'rf-tcv-compare-product-img';
        th.appendChild(img);
      }

      var nameSpan = document.createElement('div');
      nameSpan.className = 'rf-tcv-compare-product-name';
      nameSpan.textContent = product.title;
      th.appendChild(nameSpan);

      if (product.secondary) {
        var secSpan = document.createElement('div');
        secSpan.className = 'rf-tcv-compare-product-secondary';
        secSpan.textContent = product.secondary;
        th.appendChild(secSpan);
      }

      var removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'rf-tcv-compare-product-remove';
      removeBtn.setAttribute('data-tcv-tray-remove', product.id);
      removeBtn.setAttribute('aria-label', 'Remove ' + product.title + ' from compare');
      removeBtn.textContent = 'Remove';
      th.appendChild(removeBtn);

      headerRow.appendChild(th);
    }

    thead.appendChild(headerRow);
    compTable.appendChild(thead);

    // Body rows
    var tbody = document.createElement('tbody');

    // Spec rows
    for (var i = 0; i < allSpecKeys.length; i++) {
      var specKey = allSpecKeys[i];
      var specRow = document.createElement('tr');

      var specLabel = document.createElement('td');
      specLabel.className = 'rf-tcv-compare-row-label';
      specLabel.textContent = specLabels[specKey] || specKey;
      specRow.appendChild(specLabel);

      for (var j = 0; j < state.selected.length; j++) {
        var cell = document.createElement('td');
        cell.className = 'rf-tcv-compare-cell';
        var val = state.selected[j].specs[specKey] || '';
        cell.textContent = val;
        specRow.appendChild(cell);
      }

      tbody.appendChild(specRow);
    }

    compTable.appendChild(tbody);
    body.appendChild(compTable);
    panel.appendChild(body);

    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    // Focus management
    closeBtn.focus();

    // Trap focus
    document.addEventListener('keydown', panelKeyHandler);
  }

  function panelKeyHandler(e) {
    if (e.key === 'Escape') {
      closePanel();
      return;
    }

    // Focus trap
    if (e.key === 'Tab') {
      var panel = document.querySelector('[data-tcv-compare-panel]');
      if (!panel) return;
      var focusable = panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function closePanel() {
    state.panelOpen = false;
    var overlay = document.querySelector('[data-tcv-compare-overlay]');
    if (overlay) {
      overlay.parentNode.removeChild(overlay);
    }
    document.removeEventListener('keydown', panelKeyHandler);

    // Return focus to the Compare button in the tray
    var trayBtn = document.querySelector('[data-tcv-compare-open]');
    if (trayBtn) trayBtn.focus();
  }

  /* ---- selection logic ---- */

  function toggleProduct(id, addToSelection) {
    var existingIndex = -1;
    for (var i = 0; i < state.selected.length; i++) {
      if (state.selected[i].id === id) {
        existingIndex = i;
        break;
      }
    }

    if (addToSelection) {
      if (existingIndex !== -1) return; // already selected

      var max = getMaxSelection();
      if (state.selected.length >= max) {
        // Show feedback
        showLimitFeedback(max);
        return;
      }

      // Extract data from the row
      var row = findRowById(id);
      if (!row) return;

      var data = extractProductData(row);
      if (!data) return;

      state.selected.push(data);
      writeState();
      syncCheckboxes();
      updateTray();
    } else {
      if (existingIndex === -1) return;

      state.selected.splice(existingIndex, 1);
      writeState();
      syncCheckboxes();
      updateTray();
    }
  }

  function clearAll() {
    state.selected = [];
    writeState();
    syncCheckboxes();
    updateTray();
  }

  /* ---- feedback toast ---- */

  var toastTimeout = null;

  function showLimitFeedback(max) {
    var existing = document.querySelector('[data-tcv-compare-toast]');
    if (existing) return; // already showing

    var toast = document.createElement('div');
    toast.className = 'rf-tcv-compare-toast';
    toast.setAttribute('data-tcv-compare-toast', '');
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = 'Maximum ' + max + ' products. Remove one to select another.';

    document.body.appendChild(toast);

    if (toastTimeout) clearTimeout(toastTimeout);
    toastTimeout = setTimeout(function () {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 3000);
  }

  /* ---- event handling (delegated) ---- */

  function initEvents() {
    // Compare checkbox toggles
    document.addEventListener('change', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var cb = target.closest('[data-tcv-compare-toggle]');
      if (!cb) return;

      var id = cb.getAttribute('data-tcv-product-id');
      toggleProduct(id, cb.checked);
    });

    // Delegated clicks for tray, panel, etc.
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;

      // Open compare panel
      if (target.closest('[data-tcv-compare-open]')) {
        buildPanel();
        return;
      }

      // Close compare panel (button)
      if (target.closest('[data-tcv-compare-close]')) {
        closePanel();
        return;
      }

      // Close compare panel (overlay background click)
      if (target.hasAttribute && target.hasAttribute('data-tcv-compare-close-bg')) {
        closePanel();
        return;
      }

      // Clear all
      if (target.closest('[data-tcv-compare-clear]')) {
        clearAll();
        return;
      }

      // Remove individual product from tray
      var removeBtn = target.closest('[data-tcv-tray-remove]');
      if (removeBtn) {
        var id = removeBtn.getAttribute('data-tcv-tray-remove');
        toggleProduct(id, false);
        return;
      }
    });
  }

  /* ---- facets re-render handling ---- */

  function handleFacetsReRender() {
    var table = document.querySelector(TABLE_SEL);
    if (table) {
      addCompareCheckboxes(table);
      syncCheckboxes();

      // Verify selected products still exist in the current view
      var selectedIds = state.selected.map(function (p) { return p.id; });
      var visibleIds = [];
      var rows = table.querySelectorAll(ROW_SEL);
      for (var i = 0; i < rows.length; i++) {
        var link = rows[i].querySelector('.rf-tcv__product-link');
        if (link) {
          var href = link.getAttribute('href') || '';
          var handle = href.replace(/^\/products\//, '').replace(/\?.*$/, '');
          visibleIds.push(handle);
        }
      }

      // Remove products that are no longer visible (filtered out)
      var toRemove = [];
      for (var i = 0; i < state.selected.length; i++) {
        if (visibleIds.indexOf(state.selected[i].id) === -1) {
          toRemove.push(state.selected[i].id);
        }
      }

      if (toRemove.length > 0) {
        state.selected = state.selected.filter(function (p) {
          return toRemove.indexOf(p.id) === -1;
        });
        writeState();
        syncCheckboxes();
        updateTray();
      }
    }
    window.setTimeout(updateTray, 200);
  }

  /* ---- init ---- */

  function init() {
    var table = document.querySelector(TABLE_SEL);
    if (!table) return;

    // Determine collection handle
    var scope = table.closest('.rf-tcv');
    if (scope) {
      var control = scope.querySelector('[data-tcv-columns-control]');
      if (control) {
        state.collection = control.getAttribute('data-tcv-collection') || 'all';
      }
    }
    if (!state.collection) state.collection = 'all';

    // Load persisted state
    state.selected = readState();

    // Add compare checkboxes
    addCompareCheckboxes(table);
    syncCheckboxes();

    updateTray();

    // Event handling
    initEvents();

    // Listen for facet re-renders (the existing columns.js uses MutationObserver)
    if (typeof window.MutationObserver === 'function') {
      var gridContainer = document.getElementById('ProductGridContainer');
      if (gridContainer) {
        new window.MutationObserver(function (mutations) {
          handleFacetsReRender();
        }).observe(gridContainer, {
          childList: true,
          subtree: true
        });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
